/**
 * Configuration Service for Data Domain
 *
 * This service provides access to configuration data from within the data domain library
 * without creating circular dependencies with the app layer.
 *
 * Framework-agnostic: works with both @tanstack/react-query and @tanstack/solid-query.
 * The consuming app provides a cache adapter via initializeConfigurationService().
 */

/**
 * Generic cache interface that both react-query and solid-query QueryClient can implement.
 */
export interface IConfigCache {
    getQueryData(key: string[]): any;
}

let globalConfigCache: IConfigCache | null = null;

/**
 * Initialize the configuration service with a framework-agnostic cache.
 * For React Query: pass the QueryClient directly (it implements getQueryData).
 * For Solid Query: pass the QueryClient directly (it also implements getQueryData).
 */
export const initializeConfigurationService = (cache: IConfigCache): void => {
    globalConfigCache = cache;
};

/**
 * Get configuration from the query cache.
 * This can be used from within the data domain library.
 */
export const getConfigurationFromCache = (): any => {
    if (!globalConfigCache) {
        console.warn('⚠️ Configuration service not initialized. Returning empty config.');
        return {};
    }

    const config = globalConfigCache.getQueryData(['configuration']);

    if (!config) {
        console.warn('⚠️ Configuration not found in query cache. Returning empty config.');
        return {};
    }

    return config;
};

/**
 * Check if configuration service is initialized
 */
export const isConfigurationServiceInitialized = (): boolean => {
    return globalConfigCache !== null;
};
